'use client'

import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { IChildrenProps } from '@/utils/typings/children-props'
import { usePathname } from 'next/navigation'
import { UrlConfig } from '@/setup/routing/routing'
import { deriveLpVariantFromPath, ILandingPageVariant } from '@/features/landing-page/landing-page-variant'
import { getLocalStorageItem, LOCAL_STORAGE_KEYS, setLocalStorageItem } from '@/setup/local-storage'

export type IFooterCtaOverride = { label: string; href: string } | { label: string; onClick: () => void }

export interface IFooterCopyOverride {
    title: string
    titleFaded?: string
    subtitle?: string
}

interface ILandingPageContext {
    /**
     * Whether animations should be disabled.
     * True when: user navigated internally (not first page load), or on /onboarding or /auth routes.
     */
    disableAnimations: boolean
    lpVariant: ILandingPageVariant
    footerCtaOverride: IFooterCtaOverride | null
    setFooterCtaOverride: (override: IFooterCtaOverride | null) => void
    footerCopyOverride: IFooterCopyOverride | null
    setFooterCopyOverride: (override: IFooterCopyOverride | null) => void
    leadMagnetModalOpen: boolean
    setLeadMagnetModalOpen: (open: boolean) => void
}

const LandingPageContext = createContext<ILandingPageContext | null>(null)

type ILandingPageProviderProps = IChildrenProps

export function LandingPageProvider({ children }: ILandingPageProviderProps) {
    const pathname = usePathname()
    const initialPathnameRef = useRef(pathname)
    const hasNavigatedRef = useRef(false)

    // Synchronous check: once user navigates away from the initial route, mark as navigated
    if (pathname !== initialPathnameRef.current) {
        hasNavigatedRef.current = true
    }

    // Derive lpVariant from pathname only (no query param)
    const lpVariant = useMemo(() => deriveLpVariantFromPath(pathname), [pathname])

    const isLandingPath = !pathname.includes(UrlConfig['/auth'].getUrl())

    useEffect(() => {
        if (isLandingPath && getLocalStorageItem<string>(LOCAL_STORAGE_KEYS.SignUp.InitialLandingPath) === undefined) {
            setLocalStorageItem(LOCAL_STORAGE_KEYS.SignUp.InitialLandingPath, pathname)
        }
    }, [isLandingPath, pathname])

    const disableAnimations = useMemo(() => {
        if (hasNavigatedRef.current) return true

        return pathname.includes(UrlConfig['/auth'].getUrl())
    }, [pathname])

    const [footerCtaOverride, setFooterCtaOverride] = useState<IFooterCtaOverride | null>(null)
    const [footerCopyOverride, setFooterCopyOverride] = useState<IFooterCopyOverride | null>(null)
    const [leadMagnetModalOpen, setLeadMagnetModalOpen] = useState(false)

    return (
        <LandingPageContext.Provider
            value={{
                disableAnimations,
                lpVariant,
                footerCtaOverride,
                setFooterCtaOverride,
                footerCopyOverride,
                setFooterCopyOverride,
                leadMagnetModalOpen,
                setLeadMagnetModalOpen,
            }}
        >
            {children}
        </LandingPageContext.Provider>
    )
}

export function useLandingPageContext() {
    const context = useContext(LandingPageContext)

    if (!context) throw new Error('LandingPageContext not found')

    return context
}
